import { useCallback, type RefCallback } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Card, Skeleton } from '@mint-lib/tw-components';

import { useCustomMetricsDrawer } from '../../contexts/CustomMetricsContext.jsx';
import { CustomMetricCard } from '../atoms/CustomMetricCard.jsx';

export const CustomMetricsEditorList = () => {
  const { t } = useTranslation('@myn/import-hub');

  const { isLoading, customMetrics, selectedMetricDraft, selectMetric } =
    useCustomMetricsDrawer();

  const selectedCustomMetricCardRef = useCallback(
    (element: null | HTMLDivElement) => {
      if (element) {
        const rect = element.getBoundingClientRect();
        const isVisible =
          rect.top >= 0 && rect.bottom <= globalThis.innerHeight;
        if (!isVisible) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    [],
  );

  if (isLoading) {
    return <CustomMetricsListSkeleton />;
  }

  return (
    <ul
      className="m-[-8px] flex flex-1 flex-col gap-2 overflow-auto p-2"
      aria-label={t('Custom metrics list')}
    >
      <AddNewMetricCard ref={selectedCustomMetricCardRef} />
      {customMetrics.map((metric) => {
        const selected = selectedMetricDraft.id === metric.id;
        return (
          <CustomMetricCard
            key={metric.id}
            ref={selected ? selectedCustomMetricCardRef : null}
            selected={selected}
            onClick={() => selectMetric(metric.id)}
            metric={metric}
          />
        );
      })}
    </ul>
  );
};

const AddNewMetricCard = ({ ref }: { ref: RefCallback<HTMLDivElement> }) => {
  const { searchValue, isAddMetricMode, selectedMetricDraft } =
    useCustomMetricsDrawer();

  if (!isAddMetricMode || searchValue) {
    return null;
  }

  return (
    <CustomMetricCard ref={ref} metric={selectedMetricDraft} selected={true} />
  );
};

const CustomMetricsListSkeleton = () => {
  const skeletonIds = Array.from(
    { length: 20 },
    (_, index) => `custom-metric-skeleton-${index}`,
  );

  return (
    <div className="m-[-8px] flex flex-1 flex-col gap-2 p-2">
      {skeletonIds.map((skeletonId) => (
        <Card
          key={skeletonId}
          className="w-full"
          contentClassName="flex items-center gap-12 p-4 w-full"
        >
          <Skeleton variant="rounded" className="h-4 flex-1" />
          <Skeleton variant="rounded" className="h-4 basis-14" />
        </Card>
      ))}
    </div>
  );
};
